import { SmartusComponentsModule } from '@affinis/smartus-components';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Inject,
  inject,
  OnInit,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  BaseSelectViewModel,
  BillingAreaClient,
  CreateBillingAreaRequest,
  DeleteBillingAreaRequest,
  LocationClient,
  UpdateBillingAreaRequest,
} from '@core/api';
import { OverrideModule } from '@mandants/telekom/override/override.module';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ActiveToast, ToastrService } from 'ngx-toastr';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

export enum SubmitMode {
  Delete = 'delete',
  Save = 'save',
}

@Component({
  selector: 'app-edit-accounting-areas',
  standalone: true,
  imports: [
    SmartusComponentsModule,
    TranslateModule,
    FormlyModule,
    FormsModule,
    ReactiveFormsModule,
    OverrideModule,
    MatTooltipModule,
    MatDialogModule,
    CommonModule,
  ],
  templateUrl: './edit-accounting-areas.component.html',
  styleUrl: './edit-accounting-areas.component.scss',
})
export class EditAccountingAreasComponent implements OnInit {
  form = new FormGroup({});
  private readonly cd = inject(ChangeDetectorRef);

  formFields: Array<FormlyFieldConfig> = [];
  changeReasons: BaseSelectViewModel[] = [];

  formModel = {};

  isLoading = false;

  selectedCostcenter: string;

  activeToast: ActiveToast<object>;

  constructor(
    private readonly translateService: TranslateService,
    private readonly locationClient: LocationClient,
    private readonly billingAreaClient: BillingAreaClient,
    private readonly toastr: ToastrService,
    public dialogRef: MatDialogRef<EditAccountingAreasComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      title?: string;
      technicalPlace?: string; // Technischer Platz
      vbName?: string; // VB-Bezeichnung
      totalWorkplaces?: number; // Summe Arbeitsplätze (AP)
      occupiedWorkplaces?: number; // Summe belegte Arbeitsplätze (AP belegt)
      freeWorkplaces?: number; // Summe freie Arbeitsplätze (AP frei)
      areaInSquareMeters?: number; // VB-Fläche in m²
      totalCosts?: number; // VB-Gesamtkosten
      areaPerWorkplace?: number; // Fläche pro Arbeitsplatz in m²
      costPerWorkplace?: number; // Preis pro Arbeitsplatz
      changeReason?: BaseSelectViewModel; // Änderungsgrund (Dropdown)
      isEditMode: boolean;
      selectedLocation: string;
      billingAreaId: string;
    }
  ) {}

  ngOnInit() {
    forkJoin({
      changeReasons: this.locationClient.getChangeReasons().pipe(
        catchError(error => {
          console.error('Error fetching change reasons:', error);
          return of([]);
        })
      ),
    }).subscribe({
      next: results => {
        this.changeReasons = results.changeReasons;
        this.initializeFormFields();
      },
      error: error => {
        console.error('Error in ngOnInit:', error);
        this.initializeFormFields();
      },
    });
  }

  initializeFormFields() {
    this.formFields = [
      {
        type: 'input',
        key: 'technicalPlace',
        wrappers: ['readOnly'],
        defaultValue: this.data?.technicalPlace || null,
        hide: !this.data?.isEditMode,
        props: {
          label: this.translateService.instant(
            'accountingArea.accounting_areas.edit_dialog.technical_place'
          ),
        },
      },
      {
        type: 'input',
        key: 'vbName',
        defaultValue: this.data?.vbName || null,
        props: {
          label: this.translateService.instant(
            'accountingArea.accounting_areas.edit_dialog.vb_name'
          ),
          required: true,
        },
      },
      {
        type: 'chapter',
        props: {
          chapterTitle: this.translateService.instant(
            'accountingArea.accounting_areas.edit_dialog.number_workplaces'
          ),
        },
      },
      {
        type: 'flex-layout-col',
        wrappers: [],
        fieldGroup: [
          {
            type: 'input',
            key: 'totalWorkplaces',
            wrappers: ['readOnly'],
            defaultValue: this.data?.totalWorkplaces?.toString() || '0',
            props: {
              label: this.translateService.instant(
                'accountingArea.accounting_areas.edit_dialog.total_workplaces'
              ),
            },
          },
          {
            type: 'input',
            key: 'occupiedWorkplaces',
            wrappers: ['readOnly'],
            defaultValue: this.data?.occupiedWorkplaces?.toString() || '0',
            props: {
              label: this.translateService.instant(
                'accountingArea.accounting_areas.edit_dialog.total_occupied_workplaces'
              ),
            },
          },
          {
            type: 'input',
            key: 'freeWorkplaces',
            wrappers: ['readOnly'],

            defaultValue: this.data?.freeWorkplaces?.toString() || '0',
            props: {
              label: this.translateService.instant(
                'accountingArea.accounting_areas.edit_dialog.total_free_workplaces'
              ),
            },
          },
        ],
      },
      {
        type: 'chapter',
        props: {
          chapterTitle: this.translateService.instant(
            'accountingArea.accounting_areas.edit_dialog.area_key_figures'
          ),
        },
      },
      {
        type: 'flex-layout-col',
        wrappers: [],
        fieldGroup: [
          {
            type: 'input-with-text',
            key: 'areaInSquareMeters',
            wrappers: ['readOnly'],
            defaultValue: this.data?.areaInSquareMeters
              ? this.data?.areaInSquareMeters?.toString() + ' m²'
              : '0',
            props: {
              label: this.translateService.instant(
                'accountingArea.accounting_areas.edit_dialog.vb_area'
              ),
            },
          },
          {
            type: 'input',
            key: 'totalCosts',
            wrappers: ['readOnly'],
            defaultValue: this.data?.totalCosts
              ? this.data?.totalCosts?.toString() + ' €'
              : '0',
            props: {
              label: this.translateService.instant(
                'accountingArea.accounting_areas.edit_dialog.total_vb_costs'
              ),
            },
          },
        ],
      },
      {
        type: 'flex-layout-col',
        wrappers: [],
        fieldGroup: [
          {
            type: 'input-with-text',
            key: 'areaPerWorkplace',
            wrappers: ['readOnly'],
            defaultValue: this.data?.areaPerWorkplace
              ? this.data?.areaPerWorkplace?.toString() + ' m²'
              : '0',
            props: {
              label: this.translateService.instant(
                'accountingArea.accounting_areas.edit_dialog.area_per_workplace'
              ),
            },
          },
          {
            type: 'input-with-text',
            key: 'costPerWorkplace',
            wrappers: ['readOnly'],
            defaultValue: this.data?.costPerWorkplace
              ? this.data?.costPerWorkplace?.toString() + ' €'
              : '0',
            props: {
              label: this.translateService.instant(
                'accountingArea.accounting_areas.edit_dialog.cost_per_workplace'
              ),
            },
          },
        ],
      },
      {
        type: 'chapter',
      },
      {
        type: 'input-search',
        key: 'changeReason',
        defaultValue: this.data?.changeReason || null,
        props: {
          label: this.translateService.instant(
            'accountingArea.accounting_areas.edit_dialog.change_reason'
          ),
          options: this.changeReasons.map((reason: BaseSelectViewModel) => ({
            value: reason.id,
            label: reason.name,
          })),
          required: true,
        },
      },
    ];
    this.cd.detectChanges();
  }

  onCancel() {
    if (this.activeToast) {
      this.toastr.remove(this.activeToast.toastId);
    }
  }

  billingAreaEndpoint: (data) => Observable<any> = data => {
    return this.data.isEditMode
      ? this.billingAreaClient.updateBillingArea(data)
      : this.billingAreaClient.createBillingArea(data);
  };

  saveBillingArea(
    request: UpdateBillingAreaRequest | CreateBillingAreaRequest,
    successMessage: string
  ) {
    this.billingAreaEndpoint(request).subscribe({
      next: () => {
        this.isLoading = false;
        this.activeToast = this.toastr.success(
          this.translateService.instant(successMessage)
        );
        this.dialogRef.close(true);
      },
      error: err => {
        this.isLoading = false;
        let errorMessage = this.translateService.instant(
          'generic.errorMessage'
        );
        if (err.status === 409) {
          errorMessage = this.translateService.instant(
            'accountingArea.accounting_areas.edit_dialog.error_duplicate_name'
          );
        }
        this.activeToast = this.toastr.error(errorMessage);
      },
    });
  }

  deleteBillingArea(formValue) {
    const request: DeleteBillingAreaRequest = {
      billingAreaId: this.data.billingAreaId,
      changeReason: formValue.changeReason,
    };
    this.billingAreaClient.deleteBillingArea(request).subscribe({
      next: () => {
        this.isLoading = false;
        this.dialogRef.close(true);
        this.activeToast = this.toastr.success(
          this.translateService.instant(
            'accountingArea.accounting_areas.edit_dialog.delete_billing_area.success'
          )
        );
      },
      error: () => {
        this.isLoading = false;
        this.toastr.error(
          this.translateService.instant('generic.errorMessage')
        );
      },
    });
  }

  validateForm(submitMode: string) {
    if (this.form.valid) {
      const formValue = this.form.value as any;

      this.isLoading = true;
      if (submitMode === SubmitMode.Save) {
        this.prepareSubmit(formValue);
      } else if (submitMode === SubmitMode.Delete) {
        this.deleteBillingArea(formValue);
      }
    } else {
      this.form.markAllAsTouched();
    }
  }

  prepareSubmit(formValue) {
    let request: UpdateBillingAreaRequest | CreateBillingAreaRequest;
    if (this.data.isEditMode) {
      request = {
        changeReason: formValue.changeReason,
        name: formValue.vbName,
        billingAreaId: this.data.billingAreaId,
      };
      this.saveBillingArea(
        request,
        'accountingArea.accounting_areas.edit_dialog.update_billing_area.success'
      );
    } else {
      request = {
        changeReason: formValue.changeReason,
        name: formValue.vbName,
        siteId: this.data.selectedLocation,
      };
      this.saveBillingArea(
        request,
        'accountingArea.accounting_areas.edit_dialog.create_billing_area.success'
      );
    }
  }
}
